/**
 * Excel Import Utilities
 * Production-level utilities for parsing and validating Excel user import data
 */

import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import {
  ImportUserData,
  ProcessedUserData,
  ImportValidationError,
  ImportResult,
  ImportSummary,
  UserRole,
  BusinessUnit,
  Organization,
  FileValidationResult,
  ExcelColumnMapping,
  UserImportConfig,
} from "../types/UserImport";

// Default configuration for user import
export const DEFAULT_IMPORT_CONFIG: UserImportConfig = {
  maxRowsPerBatch: 100,
  allowDuplicateEmails: false,
  requirePassword: true,
  supportedFileTypes: ["xlsx", "xls"],
  maxFileSize: 5 * 1024 * 1024, // 5MB
  requiredColumns: ["firstName", "lastName", "email"],
  optionalColumns: [
    "mobileNumber",
    "password",
    "roles",
    "businessUnits",
    "organizationName",
    "isActive",
  ],
};

// Column mapping for flexible Excel formats
export const EXCEL_COLUMN_MAPPING: ExcelColumnMapping = {
  firstName: ["firstname", "first_name", "first name", "given name", "fname"],
  lastName: ["lastname", "last_name", "last name", "surname", "family name", "lname"],
  email: ["email", "email address", "e-mail", "mail"],
  mobileNumber: ["mobile", "mobile number", "phone", "phone number", "cell", "cellular"],
  password: ["password", "pwd", "pass"],
  roles: ["role", "roles", "user role", "user roles", "permissions"],
  businessUnits: [
    "business unit",
    "business units",
    "bu",
    "department",
    "dept",
    "clients",
    "client",
  ],
  organizationName: ["organization", "org", "company", "organization name"],
  isActive: ["active", "is active", "status", "enabled", "is enabled", "active (true/false)"],
};

/**
 * Validates uploaded file before processing
 */
export const validateFile = (
  file: File,
  config: UserImportConfig = DEFAULT_IMPORT_CONFIG
): FileValidationResult => {
  const result: FileValidationResult = {
    isValid: true,
    fileName: file.name,
    fileSize: file.size,
    rowCount: 0,
    columnCount: 0,
    errors: [],
    warnings: [],
  };

  // File type validation
  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  if (!fileExtension || !config.supportedFileTypes.includes(fileExtension)) {
    result.errors.push(
      `Unsupported file type. Please upload ${config.supportedFileTypes.join(" or ")} files only.`
    );
    result.isValid = false;
  }

  // File size validation
  if (file.size > config.maxFileSize) {
    result.errors.push(
      `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (${(config.maxFileSize / 1024 / 1024).toFixed(2)}MB).`
    );
    result.isValid = false;
  }

  // File name validation
  if (file.name.length > 255) {
    result.warnings.push("File name is very long. Consider using a shorter name.");
  }

  return result;
};

/**
 * Parses Excel file and extracts user data
 */
export const parseExcelFile = async (file: File): Promise<{ data: any[][]; headers: string[] }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          blankrows: false,
        }) as any[][];

        if (jsonData.length === 0) {
          reject(new Error("Excel file is empty or has no data"));
          return;
        }

        const headers = jsonData[0] as string[];
        const rowData = jsonData.slice(1); // Remove header row

        resolve({ data: rowData, headers });
      } catch (error) {
        reject(
          new Error(
            `Failed to parse Excel file: ${error instanceof Error ? error.message : "Unknown error"}`
          )
        );
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read Excel file"));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Maps Excel column names to expected field names
 */
export const mapColumnHeaders = (headers: string[]): { [key: string]: number } => {
  const mapping: { [key: string]: number } = {};
  const normalizedHeaders = headers.map((h) => h.toLowerCase().trim().replace(/[*]/g, "").trim());

  Object.entries(EXCEL_COLUMN_MAPPING).forEach(([fieldName, possibleNames]) => {
    for (let i = 0; i < normalizedHeaders.length; i++) {
      if (possibleNames.includes(normalizedHeaders[i])) {
        mapping[fieldName] = i;
        break;
      }
    }
  });

  return mapping;
};

/**
 * Converts raw Excel row data to ImportUserData
 */
export const convertRowToUserData = (
  row: any[],
  columnMapping: { [key: string]: number },
  rowIndex: number
): ImportUserData => {
  const getData = (field: string, defaultValue: any = "") => {
    const columnIndex = columnMapping[field];
    return columnIndex !== undefined ? row[columnIndex] || defaultValue : defaultValue;
  };

  // Parse roles - handle comma-separated values
  const rolesStr = getData("roles", "");
  const roles = rolesStr
    ? rolesStr
        .split(",")
        .map((r: string) => r.trim())
        .filter(Boolean)
    : [];

  // Parse business units - handle comma-separated values
  const businessUnitsStr = getData("businessUnits", "");
  const businessUnits = businessUnitsStr
    ? businessUnitsStr
        .split(",")
        .map((bu: string) => bu.trim())
        .filter(Boolean)
    : [];

  // Parse active status
  const activeStr = getData("isActive", "true").toString().toLowerCase();
  const isActive = ["true", "1", "yes", "active", "enabled"].includes(activeStr);

  return {
    firstName: getData("firstName").toString().trim(),
    lastName: getData("lastName").toString().trim(),
    email: getData("email").toString().trim().toLowerCase(),
    mobileNumber: getData("mobileNumber").toString().trim(),
    password: getData("password").toString().trim(),
    roles,
    businessUnits,
    organizationName: getData("organizationName").toString().trim(),
    isActive,
  };
};

/**
 * Validates individual user data
 */
export const validateUserData = (
  userData: ImportUserData,
  rowIndex: number,
  availableRoles: UserRole[],
  availableBusinessUnits: BusinessUnit[],
  availableOrganizations: Organization[],
  isSuperAdmin: boolean,
  config: UserImportConfig = DEFAULT_IMPORT_CONFIG
): ImportValidationError[] => {
  const errors: ImportValidationError[] = [];

  // Required field validation
  if (!userData.firstName.trim()) {
    errors.push({
      row: rowIndex,
      field: "firstName",
      value: userData.firstName,
      message: "First name is required",
      type: "error",
    });
  }

  if (!userData.lastName.trim()) {
    errors.push({
      row: rowIndex,
      field: "lastName",
      value: userData.lastName,
      message: "Last name is required",
      type: "error",
    });
  }

  // Email validation
  if (!userData.email.trim()) {
    errors.push({
      row: rowIndex,
      field: "email",
      value: userData.email,
      message: "Email is required",
      type: "error",
    });
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      errors.push({
        row: rowIndex,
        field: "email",
        value: userData.email,
        message: "Invalid email format",
        type: "error",
      });
    }
  }

  // Password validation
  if (config.requirePassword && !userData.password.trim()) {
    if (!config.defaultPassword) {
      errors.push({
        row: rowIndex,
        field: "password",
        value: userData.password,
        message: "Password is required",
        type: "error",
      });
    }
  } else if (userData.password.trim()) {
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    if (!passwordRegex.test(userData.password)) {
      errors.push({
        row: rowIndex,
        field: "password",
        value: userData.password,
        message:
          "Password must be 8+ characters with an uppercase letter, a number, and a special character",
        type: "error",
      });
    }
  }

  // Role validation
  if (userData.roles.length === 0) {
    errors.push({
      row: rowIndex,
      field: "roles",
      value: userData.roles,
      message: "At least one role is required",
      type: "error",
    });
  } else {
    const validRoleNames = availableRoles.map((r) => r.name.toLowerCase());
    const invalidRoles = userData.roles.filter(
      (role) => !validRoleNames.includes(role.toLowerCase())
    );

    if (invalidRoles.length > 0) {
      errors.push({
        row: rowIndex,
        field: "roles",
        value: invalidRoles,
        message: `Invalid roles: ${invalidRoles.join(", ")}. Valid roles are: ${availableRoles.map((r) => r.name).join(", ")}`,
        type: "error",
      });
    }

    // Check for conflicting roles (Admin cannot have other roles)
    if (userData.roles.some((r) => r.toLowerCase() === "admin") && userData.roles.length > 1) {
      errors.push({
        row: rowIndex,
        field: "roles",
        value: userData.roles,
        message: "Admin role cannot be combined with other roles",
        type: "error",
      });
    }
  }

  // Business unit validation
  if (userData.businessUnits.length === 0) {
    errors.push({
      row: rowIndex,
      field: "businessUnits",
      value: userData.businessUnits,
      message: "At least one client is required",
      type: "error",
    });
  } else {
    const validBusinessUnitNames = availableBusinessUnits
      .filter((bu) => !bu.isDeleted)
      .map((bu) => bu.name.toLowerCase());

    const invalidBusinessUnits = userData.businessUnits.filter(
      (bu) => !validBusinessUnitNames.includes(bu.toLowerCase())
    );

    if (invalidBusinessUnits.length > 0) {
      errors.push({
        row: rowIndex,
        field: "businessUnits",
        value: invalidBusinessUnits,
        message: `Invalid clients: ${invalidBusinessUnits.join(", ")}`,
        type: "error",
      });
    }
  }

  // Organization validation (for super admin)
  if (isSuperAdmin && userData.organizationName && userData.organizationName.trim()) {
    const validOrgNames = availableOrganizations.map((org) => org.name.toLowerCase());
    if (!validOrgNames.includes(userData.organizationName.toLowerCase())) {
      errors.push({
        row: rowIndex,
        field: "organizationName",
        value: userData.organizationName,
        message: `Invalid organization: ${userData.organizationName}`,
        type: "error",
      });
    }
  }

  // Mobile number validation (if provided)
  if (userData.mobileNumber && userData.mobileNumber.trim()) {
    const mobileRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!mobileRegex.test(userData.mobileNumber.replace(/[\s\-\(\)]/g, ""))) {
      errors.push({
        row: rowIndex,
        field: "mobileNumber",
        value: userData.mobileNumber,
        message: "Invalid mobile number format",
        type: "warning",
      });
    }
  }

  return errors;
};

/**
 * Processes validated user data into API format
 */
export const processUserData = (
  userData: ImportUserData,
  availableRoles: UserRole[],
  availableBusinessUnits: BusinessUnit[],
  availableOrganizations: Organization[],
  currentUserOrganizationId: string,
  createdBy: string,
  isSuperAdmin: boolean,
  config: UserImportConfig = DEFAULT_IMPORT_CONFIG
): ProcessedUserData => {
  // Map roles
  const roles = userData.roles.map((roleName) => {
    const role = availableRoles.find((r) => r.name.toLowerCase() === roleName.toLowerCase());
    return { roleId: role!.id };
  });

  // Map business units
  const businessUnits = userData.businessUnits.map((buName) => {
    const businessUnit = availableBusinessUnits.find(
      (bu) => bu.name.toLowerCase() === buName.toLowerCase() && !bu.isDeleted
    );
    return { businessUnitId: businessUnit!.id };
  });

  // Determine organization ID
  let organizationId = currentUserOrganizationId;
  if (isSuperAdmin && userData.organizationName && userData.organizationName.trim()) {
    const organization = availableOrganizations.find(
      (org) => org.name.toLowerCase() === userData.organizationName!.toLowerCase()
    );
    if (organization) {
      organizationId = organization.id;
    }
  }

  // Use default password if none provided and default is configured
  const password = userData.password.trim() || config.defaultPassword || "";

  return {
    firstName: userData.firstName.trim(),
    lastName: userData.lastName.trim(),
    email: userData.email.trim().toLowerCase(),
    mobileNumber: userData.mobileNumber?.trim() || undefined,
    password,
    roles,
    businessUnits,
    organizationId,
    isActive: userData.isActive,
    createdBy,
  };
};

/**
 * Processes Excel file and validates all user data
 */
export const processExcelImport = async (
  file: File,
  availableRoles: UserRole[],
  availableBusinessUnits: BusinessUnit[],
  availableOrganizations: Organization[],
  currentUserOrganizationId: string,
  createdBy: string,
  isSuperAdmin: boolean,
  config: UserImportConfig = DEFAULT_IMPORT_CONFIG
): Promise<ImportSummary> => {
  // Validate file
  const fileValidation = validateFile(file, config);
  if (!fileValidation.isValid) {
    throw new Error(fileValidation.errors.join(", "));
  }

  // Parse Excel file
  const { data, headers } = await parseExcelFile(file);

  if (data.length === 0) {
    throw new Error("Excel file contains no data rows");
  }

  if (data.length > config.maxRowsPerBatch) {
    throw new Error(
      `Too many rows. Maximum allowed: ${config.maxRowsPerBatch}, found: ${data.length}`
    );
  }

  // Map column headers
  const columnMapping = mapColumnHeaders(headers);

  // Check required columns
  const missingRequiredColumns = config.requiredColumns.filter(
    (col) => columnMapping[col] === undefined
  );

  if (missingRequiredColumns.length > 0) {
    throw new Error(`Missing required columns: ${missingRequiredColumns.join(", ")}`);
  }

  // Process each row
  const results: ImportResult[] = [];
  const emails = new Set<string>();
  const duplicateEmails: string[] = [];

  for (let i = 0; i < data.length; i++) {
    const rowIndex = i + 2; // Excel row number (1-based + header)

    try {
      // Convert row to user data
      const userData = convertRowToUserData(data[i], columnMapping, rowIndex);

      // Check for duplicate emails within the file
      if (emails.has(userData.email)) {
        duplicateEmails.push(userData.email);
      }
      emails.add(userData.email);

      // Validate user data
      const errors = validateUserData(
        userData,
        rowIndex,
        availableRoles,
        availableBusinessUnits,
        availableOrganizations,
        isSuperAdmin,
        config
      );

      const validationErrors = errors.filter((e) => e.type === "error");
      const warnings = errors.filter((e) => e.type === "warning");

      let processed: ProcessedUserData | undefined;
      if (validationErrors.length === 0) {
        try {
          processed = processUserData(
            userData,
            availableRoles,
            availableBusinessUnits,
            availableOrganizations,
            currentUserOrganizationId,
            createdBy,
            isSuperAdmin,
            config
          );
        } catch (error) {
          validationErrors.push({
            row: rowIndex,
            field: "general",
            value: userData,
            message: `Processing error: ${error instanceof Error ? error.message : "Unknown error"}`,
            type: "error",
          });
        }
      }

      results.push({
        success: validationErrors.length === 0,
        user: userData,
        errors: validationErrors,
        warnings,
        processed,
      });
    } catch (error) {
      results.push({
        success: false,
        user: {} as ImportUserData,
        errors: [
          {
            row: rowIndex,
            field: "general",
            value: data[i],
            message: `Row processing error: ${error instanceof Error ? error.message : "Unknown error"}`,
            type: "error",
          },
        ],
        warnings: [],
      });
    }
  }

  const successCount = results.filter((r) => r.success).length;
  const errorCount = results.filter((r) => !r.success).length;
  const warningCount = results.reduce((sum, r) => sum + r.warnings.length, 0);

  return {
    totalRows: data.length,
    successCount,
    errorCount,
    warningCount,
    results,
    duplicateEmails,
  };
};

/**
 * Generates Excel template for user import
 */
export const generateExcelTemplate = async (
  availableRoles: UserRole[],
  availableBusinessUnits: BusinessUnit[],
  availableOrganizations: Organization[],
  isSuperAdmin: boolean
): Promise<Blob> => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Users");

  const activeBUs = availableBusinessUnits.filter((bu) => !bu.isDeleted).map((bu) => bu.name);
  const activeRoles = availableRoles.map((r) => r.name);

  // --- Column definitions ---
  const columns: { header: string; key: string; width: number }[] = [
    { header: "First Name *", key: "firstName", width: 18 },
    { header: "Last Name *", key: "lastName", width: 18 },
    { header: "Email *", key: "email", width: 28 },
    { header: "Mobile Number", key: "mobile", width: 18 },
    { header: "Password *", key: "password", width: 20 },
    { header: "Roles *", key: "roles", width: 18 },
    { header: "Clients *", key: "clients", width: 20 },
  ];

  if (isSuperAdmin) {
    columns.push({ header: "Organization", key: "org", width: 22 });
  }

  columns.push({ header: "Active *", key: "active", width: 12 });

  sheet.columns = columns;

  // --- Header row styling (light blue background, bold dark text) ---
  const headerRow = sheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFC5E3F7" },
    };
    cell.font = { bold: true, color: { argb: "FF1A3A5C" }, size: 11 };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FF2AA7E0" } },
    };
  });
  headerRow.height = 22;

  // Freeze header row
  sheet.views = [{ state: "frozen", xSplit: 0, ySplit: 1 }];

  // --- Sample data rows ---
  const sampleRows = [
    {
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      mobile: "+1234567890",
      password: "SecurePass123!",
      roles: activeRoles[0] || "Maker",
      clients: activeBUs[0] || "Finance",
      ...(isSuperAdmin ? { org: availableOrganizations[0]?.name || "Main Organization" } : {}),
      active: "true",
    },
    {
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      mobile: "+1234567891",
      password: "SecurePass456!",
      roles: activeRoles[1] || activeRoles[0] || "Checker",
      clients: activeBUs[1] || activeBUs[0] || "HR",
      ...(isSuperAdmin ? { org: availableOrganizations[0]?.name || "Main Organization" } : {}),
      active: "true",
    },
  ];

  sampleRows.forEach((row) => sheet.addRow(row));

  // --- Column index helpers ---
  const rolesColNum = 6; // F
  const clientsColNum = 7; // G
  const activeColNum = isSuperAdmin ? 9 : 8;
  const lastDataRow = 502;

  // --- Roles dropdown ---
  if (activeRoles.length > 0) {
    for (let r = 2; r <= lastDataRow; r++) {
      sheet.getCell(r, rolesColNum).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${activeRoles.join(",")}"`],
        showErrorMessage: true,
        errorTitle: "Invalid Role",
        error: "Please select a role from the dropdown.",
      };
    }
  }

  // --- Clients dropdown ---
  if (activeBUs.length > 0) {
    // Use a hidden reference sheet when the list is long to avoid 255-char Excel limit
    const buCsv = activeBUs.join(",");
    if (buCsv.length <= 255) {
      for (let r = 2; r <= lastDataRow; r++) {
        sheet.getCell(r, clientsColNum).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${buCsv}"`],
          showErrorMessage: true,
          errorTitle: "Invalid Client",
          error: "Please select a client from the dropdown.",
        };
      }
    } else {
      // Put values in a hidden sheet and reference it
      const refSheet = workbook.addWorksheet("_BU_List");
      refSheet.state = "veryHidden";
      activeBUs.forEach((name, i) => {
        refSheet.getCell(i + 1, 1).value = name;
      });
      const rangeRef = `_BU_List!$A$1:$A$${activeBUs.length}`;
      for (let r = 2; r <= lastDataRow; r++) {
        sheet.getCell(r, clientsColNum).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [rangeRef],
          showErrorMessage: true,
          errorTitle: "Invalid Client",
          error: "Please select a client from the dropdown.",
        };
      }
    }
  }

  // --- Active dropdown ---
  for (let r = 2; r <= lastDataRow; r++) {
    sheet.getCell(r, activeColNum).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: ['"true,false"'],
      showErrorMessage: true,
      errorTitle: "Invalid Value",
      error: "Please select true or false.",
    };
  }

  // --- Write to Blob ---
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
